/**
 * Parse a date coming from the API without letting it drift a day.
 *
 * `new Date('2026-06-15')` is specified to parse as UTC midnight. Every
 * timezone west of Greenwich then renders it as the previous day — in
 * Santo Domingo (GMT-4) an appointment booked for the 15th shows as the
 * 14th, and a birth date of 2000-01-01 becomes 1999-12-31, which changes
 * the calculated age.
 *
 * Date-only strings are therefore built from their parts as a local date.
 * Values carrying a time keep native parsing, where the offset is real
 * information rather than an artefact.
 *
 * Returns null instead of an Invalid Date so callers can branch on it.
 */
export const parseApiDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  // Copy rather than hand back the caller's instance: returning an alias
  // means a later setDate() on the result silently mutates their Date too.
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  const trimmed = value.trim();
  // Laravel `date` casts serialize as midnight UTC (`2026-08-20T00:00:00.000000Z`).
  // Native parsing in America/Santo_Domingo rolls that back one calendar day.
  const laravelDate = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?Z)?$/.exec(trimmed);
  const parsed = laravelDate
    ? new Date(Number(laravelDate[1]), Number(laravelDate[2]) - 1, Number(laravelDate[3]))
    : new Date(trimmed);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** dd/mm/yyyy, or a dash when there is nothing valid to show. */
export const formatDate = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string => {
  const date = parseApiDate(value);
  return date ? date.toLocaleDateString('es-DO', options) : '-';
};

/**
 * Sex of a patient, or an explicit "unknown" when the record has none.
 *
 * `patients.gender` is nullable and 515 of the 4,888 records migrated from
 * iDAMedic carry NULL. The type used to declare it `boolean`, so callers
 * wrote `gender ? 'Masculino' : 'Femenino'` and every one of those records
 * rendered as female. Sex drives dosing and reference ranges, so an
 * incomplete record has to look incomplete rather than plausible.
 */
export const formatGender = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return 'No especificado';
  return value ? 'Masculino' : 'Femenino';
};

/** Avatar glyph and colours matching `formatGender`, neutral when unknown. */
export const genderIcon = (
  value: boolean | null | undefined,
): { className: string; color: string; background: string } => {
  if (value === null || value === undefined) {
    return { className: 'fa-genderless', color: '#64748b', background: '#e2e8f0' };
  }
  return value
    ? { className: 'fa-mars', color: '#3b82f6', background: '#dbeafe' }
    : { className: 'fa-venus', color: '#ec4899', background: '#fce7f3' };
};

export const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return `RD$ ${num.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
